import React, { useState, useEffect } from "react";
import { InviteableUser, getInviteableUsers, patchBooking } from "api/bookings";
import { DetailedBooking } from "api/admin/bookings";
import { useSnackbar } from "notistack";
import { Paper, Select, MenuItem, FormControl, InputLabel } from "@material-ui/core";
import { Column, Query, MTableToolbar, Action } from "material-table";
import { DefaultTable } from "components";
import { useMounted } from "utils/hooks";
import i18n from "../i18n";

// prop types
interface InviteParticipantsProps {
    booking?: DetailedBooking,
    onCancel: () => void,
    onRefreshBooking: () => void,
}

// participant roles
const participantRoles = ["standard", "coach"] as const;

// participants tab in detail dialog
const InviteParticipants = ({ booking, onCancel, onRefreshBooking }: InviteParticipantsProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [isLoading, setLoading] = useState(false);
    const [participantRole, setParticipantRole] = useState<"standard" | "coach">("standard");

    // invite action
    const inviteAction = async (e: React.MouseEvent, players: InviteableUser[]) => {
        setLoading(true);
        try {
            await patchBooking({
                bookingId: booking!.id,
                newParticipants: players.map((p) => ({
                    userId: p.id,
                    participantRole,
                })),
            });
            onRefreshBooking();
            onCancel();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<InviteableUser>) => {
        // paging
        const params: Parameters<typeof getInviteableUsers>[0] = {
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };

        // filter
        params.courtId = booking?.court.id;
        params.inviteableAsCoachOnly = (participantRole === "coach");
        params.inviteableOnly = true;
        params.query = query.search;

        return params;
    };

    // table actions
    const tableActions: Action<InviteableUser>[] = [
        {
            icon: "edit",
            isFreeAction: true,
            tooltip: i18n.manageParticipants,
            onClick: onCancel,
        },
        {
            icon: "check",
            tooltip: i18n.invite,
            onClick: (e, players) => inviteAction(e, players as InviteableUser[]),
        },
    ];

    // table columns
    const tableColumns: Column<InviteableUser>[] = [
        {
            field: "firstName",
            title: i18n.firstName,
        },
        {
            field: "lastName",
            title: i18n.lastName,
        },
    ];

    // refresh (-> remote data)
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange();
    };
    useEffect(refreshTable, [participantRole]);

    // render
    return (
        <DefaultTable
            actions={tableActions}
            columns={tableColumns}
            components={{
                Container: (props) => <Paper {...props} variant="outlined" />,
                Toolbar: (props) => (
                    <>
                        <MTableToolbar {...props} />
                        {booking?.bookingType === "training" && (
                            <FormControl style={{ margin: "8px 24px", minWidth: "240px" }}>
                                <InputLabel>{i18n.participantRole}</InputLabel>
                                <Select
                                    label={i18n.participantRole}
                                    value={participantRole}
                                    onChange={(e) => setParticipantRole(e.target.value as typeof participantRole)}
                                >
                                    {participantRoles.map((r) => (
                                        <MenuItem key={r} value={r}>{i18n.participantRoles[r]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </>
                ),
            }}
            data={(query) => new Promise((resolve) => {
                const params = convertQueryToParams(query);
                getInviteableUsers(params)
                    .then((response) => resolve({
                        ...response,
                        page: query.page,
                    }))
                    .catch((err) => {
                        displayError(err);
                        resolve({ data: [], page: 0, totalCount: 0 });
                    });
            })}
            isLoading={isLoading}
            options={{ sorting: false, selection: true }}
            tableRef={tableRef}
            title={i18n.invitePlayers}
        />
    );
};

export default InviteParticipants;
